<i18n>
{
  "fr": {
    "firstname-label": "Prénom",
    "lastname-label": "Nom",
    "email-label": "Courriel",
    "email-edit": "Modifier",
    "position-label": "Poste occupé",
    "user-type-label": "Sélectionnez le rôle que vous donnez à cet utilisateur:",
    "user-type-admin-label": "Administrateur",
    "user-type-admin-description": "Les administrateurs peuvent accéder à toutes les sections et inviter d'autres utilisateurs au compte entreprise.",
    "user-type-business-editor-label": "Éditeur section « conformité générale »",
    "user-type-project-editor-label": "Éditeur section « conformité par projet »",
    "user-type-global-editor-label": "Éditeur sections « conformité générale » et « conformité par projet »",
    "user-type-change-warning": "En modifiant le rôle, si l'utilisateur est assigné à des indicateurs qui ne lui sont plus accessibles, ceux-ci se retrouveront sans responsable.",
    "user-type-change-self": "Vous ne pouvez pas modifier votre propre rôle.",
    "update-button": "Sauvegarder",
    "create-button": "Envoyer l'invitation",
    "create-button-subtext": "L'invitation sera envoyée à l'utilisateur par courriel.",
    "error-user-firstname-empty": "Le prénom est obligatoire",
    "error-user-lastname-empty": "Le nom est obligatoire",
    "error-user-email-empty": "L'adresse courriel est obligatoire",
    "error-user-email-invalid": "L'adresse courriel est invalide",
    "error-user-position-empty": "Le poste est obligatoire",
    "error-user-type-empty": "Veuillez choisir un rôle",
    "language-label": "Langue de communication",
    "language-placeholder": "Assigner une langue",
    "error-user-language-empty": "La langue de communication est obligatoire"
  },
  "en": {
    "firstname-label": "First name",
    "lastname-label": "Last name",
    "email-label": "Email",
    "email-edit": "Change",
    "position-label": "Position held",
    "user-type-label": "Select the role you give to this user:",
    "user-type-admin-label": "Administrator",
    "user-type-admin-description": "Administrators can access every sections and invite new users in a company.",
    "user-type-business-editor-label": "Editor - \"General compliance\"",
    "user-type-project-editor-label": "Editor - \"Project compliance\"",
    "user-type-global-editor-label": "Editor - \"General compliance\" and \"Project compliance\"",
    "user-type-change-warning": "By changing the role, if the user is assigned to indicators that are no longer accessible to them, they will be left unassigned.",
    "user-type-change-self": "You may not change your own role.",
    "update-button": "Save",
    "create-button": "Send invite",
    "create-button-subtext": "The invitation will be sent to the user by email.",
    "error-user-firstname-empty": "The first name is required",
    "error-user-lastname-empty": "The last name is required",
    "error-user-email-empty": "The email address is required",
    "error-user-email-invalid": "The email address is invalid",
    "error-user-position-empty": "The position is required",
    "error-user-type-empty": "Please choose a role",
    "language-label": "Language of communication",
    "language-placeholder": "Set a language",
    "error-user-language-empty": "The language of communication is mandatory"
  }
}
</i18n>

<template>
  <div>
    <s-form ref="form" @submit="onSubmit">
      <s-field
        class="has-max-width-480"
        :label="$t('firstname-label')"
        :value="value.firstName"
        :rules="rules.firstName"
        validate-on-input
      >
        <b-input v-model="value.firstName" />
      </s-field>
      <s-field
        class="has-max-width-480"
        :label="$t('lastname-label')"
        :value="value.lastName"
        :rules="rules.lastName"
        validate-on-input
      >
        <b-input v-model="value.lastName" />
      </s-field>
      <s-field class="has-max-width-480" :label="$t('email-label')" :value="value.email" :rules="rules.email" validate-on-input>
        <b-input v-model="value.email" :disabled="isEdit" expanded />
        <p class="control" v-if="showChangeEmail">
          <b-button type="is-primary is-light" icon-left="pen" :label="$t('email-edit')" @click="openChangeEmail" />
        </p>
      </s-field>
      <s-field
        class="has-max-width-480"
        :label="$t('position-label')"
        :value="value.position"
        :rules="rules.position"
        validate-on-input
      >
        <b-input v-model="value.position" />
      </s-field>
      <s-field
        v-if="!hideRoles"
        class="has-max-width-768"
        :class="{
          'mt-24px': $media.mobile,
          'mt-32px': $media.tablet,
          'mt-48px': $media.widescreen
        }"
        :label="$t('user-type-label')"
        :value="value.type"
        :rules="rules.type"
        validate-on-input
      >
        <div>
          <b-message v-if="isSelf">{{ $t('user-type-change-self') }}</b-message>
          <b-message v-else-if="isEdit" type="is-warning">{{ $t('user-type-change-warning') }}</b-message>

          <div class="mb-8px">
            <b-radio v-model="value.type" :native-value="$consts.enums.BUSINESS_USER_TYPE_BUSINESS_ADMIN" :disabled="isSelf">
              {{ $t('user-type-admin-label') }}
              <span class="is-block is-italic is-size-7">{{ $t('user-type-admin-description') }}</span>
            </b-radio>
          </div>
          <div class="mb-4px">
            <b-radio v-model="value.type" :native-value="$consts.enums.BUSINESS_USER_TYPE_BUSINESS_EDITOR" :disabled="isSelf">
              {{ $t('user-type-business-editor-label') }}
            </b-radio>
          </div>
          <div class="mb-4px">
            <b-radio v-model="value.type" :native-value="$consts.enums.BUSINESS_USER_TYPE_PROJECT_EDITOR" :disabled="isSelf">
              {{ $t('user-type-project-editor-label') }}
            </b-radio>
          </div>
          <div class="mb-4px">
            <b-radio v-model="value.type" :native-value="$consts.enums.BUSINESS_USER_TYPE_GLOBAL_EDITOR" :disabled="isSelf">
              {{ $t('user-type-global-editor-label') }}
            </b-radio>
          </div>
        </div>
      </s-field>

      <!--Langue de communication-->
      <s-field :label="$t('language-label')" :value="value.language" :rules="rules.language" class="has-max-width-480">
        <b-select expanded :placeholder="$t('language-placeholder')" v-model="value.language">
          <option v-for="i in userLanguages" :key="i.value" :value="i.value">{{ i.text }}</option>
        </b-select>
      </s-field>

      <div
        class="content"
        :class="{
          'mt-24px': $media.mobile,
          'mt-32px': $media.tablet,
          'mt-48px': $media.widescreen
        }"
      >
        <div v-if="isEdit">
          <p>
            <b-button type="is-link" native-type="submit" :loading="loading">{{ $t('update-button') }}</b-button>
          </p>
        </div>
        <div v-else>
          <p>
            <b-button type="is-link" native-type="submit" :loading="loading">{{ $t('create-button') }}</b-button>
          </p>
          <p class="has-max-width-768">{{ $t('create-button-subtext') }}</p>
        </div>
      </div>
    </s-form>
    <change-email v-if="showChangeEmail" ref="changeEmail" :loading="loading" @change-email="onChangeEmail" />
  </div>
</template>
<script>
import FormHelper from '@/helpers/form'
import SForm from '@/components/form/form'
import SField from '@/components/form/field'
import ChangeEmail from '@/components/businesses/change-email'

import { USER_LANGUAGE_FRENCH, USER_LANGUAGE_ENGLISH } from '@/consts/enums'
import EnumHelper from '@/helpers/enums'

export default {
  components: { SForm, SField, ChangeEmail },
  props: {
    isEdit: Boolean,
    isSelf: Boolean,
    hideRoles: Boolean,
    showChangeEmail: Boolean,
    loading: Boolean,
    value: Object
  },
  computed: {
    rules() {
      return {
        firstName: [(value) => FormHelper.notEmpty(value) || this.$t('error-user-firstname-empty')],
        lastName: [(value) => FormHelper.notEmpty(value) || this.$t('error-user-lastname-empty')],
        email: [
          (value) => FormHelper.notEmpty(value) || this.$t('error-user-email-empty'),
          (value) => FormHelper.isEmail(value) || this.$t('error-user-email-invalid')
        ],
        position: [(value) => FormHelper.notEmpty(value) || this.$t('error-user-position-empty')],
        type: [(value) => !!value || this.$t('error-user-type-empty')],
        language: [(value) => !FormHelper.stringEmpty(value) || this.$t('error-user-language-empty')]
      }
    },
    userLanguages() {
      const languages = [USER_LANGUAGE_FRENCH, USER_LANGUAGE_ENGLISH]

      return languages.map((x) => {
        return {
          value: x,
          text: EnumHelper.formatUserLanguage(x)
        }
      })
    }
  },
  methods: {
    onSubmit() {
      if (!this.$refs.form.validate()) return

      this.$emit('submit')
    },
    openChangeEmail() {
      this.$refs.changeEmail.open()
    },
    onChangeEmail(email) {
      const successCallback = () => this.$refs.changeEmail.close()
      this.$emit('change-email', email, successCallback)
    }
  }
}

export function mapGraphToModel(graphData) {
  const { firstName, lastName, language } = graphData.user.profile
  return {
    position: graphData.position,
    type: graphData.userType,
    email: graphData.user.email,
    firstName: firstName,
    lastName: lastName,
    language: language
  }
}
</script>

<fragments>
fragment formFields on UserBusinessGraphType {
  position
  userType
  user {
    id
    email
    profile {
      id
      firstName
      lastName
      language
    }
  }
}
</fragments>